import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import StatCard from "../components/StatCard";
import api, { formatCurrency, formatQty } from "../lib/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { AlertTriangle, MapPin, Crown, Coins, Bell, Building2, Store, ArrowLeftRight, Sparkles, FileWarning, Video } from "lucide-react";

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [commWidgets, setCommWidgets] = useState(null);
  const [warehouseSummary, setWarehouseSummary] = useState(null);

  useEffect(() => {
    api.get("/dashboard/kpis").then(({ data }) => setKpis(data));
    api.get("/accounting/monthly").then(({ data }) => setMonthly(data)).catch(() => {});
    api.get("/commissions/widgets").then(({ data }) => setCommWidgets(data)).catch(() => {});
    api.get("/products/transfers/summary").then(({ data }) => setWarehouseSummary(data)).catch(() => {});
  }, []);

  if (!kpis) {
    return (
      <Layout title="Dashboard" subtitle="Atelier overview">
        <div className="overline">Loading…</div>
      </Layout>
    );
  }

  // Seller view (sales role) — different KPIs
  if (kpis.role_view === "seller") {
    return <SellerDashboardView kpis={kpis} commWidgets={commWidgets} />;
  }

  return (
    <Layout title="Atelier Overview" subtitle="Real-time house metrics">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          testid="kpi-inventory-value"
          overline="Inventory Value"
          value={formatCurrency(kpis.inventory_value)}
          sub={`Retail: ${formatCurrency(kpis.retail_value)}`}
          accent
        />
        <StatCard
          testid="kpi-monthly-revenue"
          overline="Monthly Revenue"
          value={formatCurrency(kpis.monthly_revenue)}
          sub={`${kpis.sold_count} sold this period`}
        />
        {/* Net Profit + Margin % only shown when backend exposes them
            (admin + accountant). Staff/sales never see cost-derived numbers. */}
        {kpis.net_profit != null && (
          <StatCard
            testid="kpi-net-profit"
            overline="Net Profit"
            value={formatCurrency(kpis.net_profit)}
            sub={`Gross ${formatCurrency(kpis.monthly_profit_gross)} · Exp ${formatCurrency(kpis.monthly_expenses)}`}
            accent
          />
        )}
        <StatCard
          testid="kpi-total-products"
          overline="Pieces in House"
          value={kpis.total_products}
          sub={`${kpis.available_count} avail · ${kpis.reserved_count} reserved`}
        />
        {kpis.gross_margin_pct != null && (
          <StatCard
            testid="kpi-gross-margin"
            overline="Gross Margin"
            value={`${kpis.gross_margin_pct.toFixed(1)}%`}
            sub={`Net ${kpis.net_margin_pct?.toFixed(1) ?? "0.0"}% after expenses`}
            accent
          />
        )}
      </div>

      {/* Warehouse summary (multi-warehouse system) */}
      {warehouseSummary && (
        <div className="mt-8" data-testid="warehouse-summary">
          <div className="overline mb-3">Warehouses</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            <div className="glass-card p-7" data-testid="warehouse-online-tile">
              <div className="overline flex items-center gap-2 text-[#D4AF37]">
                <Building2 className="w-3 h-3" /> Online Sale Inventory
              </div>
              <div className="serif text-3xl mt-3">{formatCurrency(warehouseSummary.warehouses.ONLINE_SALE.value)}</div>
              <div className="mono text-xs text-[#8A8A8A] mt-2">
                {warehouseSummary.warehouses.ONLINE_SALE.count} {warehouseSummary.warehouses.ONLINE_SALE.count === 1 ? "piece" : "pieces"}
              </div>
            </div>
            <div className="glass-card p-7" data-testid="warehouse-showroom-tile">
              <div className="overline flex items-center gap-2 text-[#7BA585]">
                <Store className="w-3 h-3" /> Showroom Inventory
              </div>
              <div className="serif text-3xl mt-3">{formatCurrency(warehouseSummary.warehouses.SHOWROOM.value)}</div>
              <div className="mono text-xs text-[#8A8A8A] mt-2">
                {warehouseSummary.warehouses.SHOWROOM.count} {warehouseSummary.warehouses.SHOWROOM.count === 1 ? "piece" : "pieces"}
              </div>
            </div>
            <div className="glass-card p-7" data-testid="warehouse-live-tile">
              <div className="overline flex items-center gap-2 text-[#C99356]">
                <Video className="w-3 h-3" /> Live Product Stock
              </div>
              <div className="serif text-3xl mt-3">{formatCurrency(warehouseSummary.warehouses.LIVE_PRODUCT?.value || 0)}</div>
              <div className="mono text-xs text-[#8A8A8A] mt-2">
                {warehouseSummary.warehouses.LIVE_PRODUCT?.count || 0} {(warehouseSummary.warehouses.LIVE_PRODUCT?.count || 0) === 1 ? "piece" : "pieces"}
              </div>
            </div>
            <div className="glass-card p-7" data-testid="warehouse-transfer-tile">
              <div className="overline flex items-center gap-2 text-[#C99356]">
                <ArrowLeftRight className="w-3 h-3" /> Items in Transfer
              </div>
              <div className="serif text-3xl mt-3">{warehouseSummary.items_in_transfer_7d}</div>
              <div className="mono text-xs text-[#8A8A8A] mt-2">Last 7 days</div>
            </div>
            <div className="glass-card p-7" data-testid="warehouse-fastmover-tile">
              <div className="overline flex items-center gap-2 text-[#F3E5AB]">
                <Sparkles className="w-3 h-3" /> Fast Moving Display Stones
              </div>
              {warehouseSummary.fast_moving_showroom.length === 0 ? (
                <div className="mt-3 text-sm text-[#666] italic">No showroom sales in last 30 days yet.</div>
              ) : (
                <div className="mt-3 space-y-1.5">
                  {warehouseSummary.fast_moving_showroom.slice(0, 3).map((m) => (
                    <div key={m._id} className="flex justify-between items-baseline text-xs">
                      <div className="truncate flex-1 mr-2 text-[#EAEAEA]" title={m.product_name}>{m.product_name}</div>
                      <div className="mono text-[#D4AF37]">{formatCurrency(m.revenue)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---- Memo & Receivables Alerts (admin only) ---- */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5" data-testid="memo-receivables-row">
        <StatCard
          testid="kpi-stones-on-memo"
          overline="Stones On Memo"
          value={kpis.stones_on_memo ?? 0}
          sub={`Value ${formatCurrency(kpis.on_memo_value || 0)}`}
        />
        <StatCard
          testid="kpi-overdue-memo"
          overline="Overdue Memos"
          value={kpis.overdue_memo_count ?? 0}
          sub={kpis.overdue_memo_count > 0 ? `${formatCurrency(kpis.overdue_value || 0)} past due` : "All on track"}
          accent={kpis.overdue_memo_count > 0}
        />
        <StatCard
          testid="kpi-partial-receivables"
          overline="Partial Payment Receivables"
          value={formatCurrency(kpis.partial_receivables || 0)}
          sub={`${kpis.partial_sales_count || 0} open invoice(s)`}
        />
        <StatCard
          testid="kpi-owed-to-suppliers"
          overline="Owed to Suppliers"
          value={formatCurrency(kpis.pending_owner_payments || 0)}
          sub={`${kpis.pending_owner_count || 0} open purchase(s)`}
          accent={(kpis.pending_owner_count || 0) > 0}
        />
        <StatCard
          testid="kpi-total-outstanding"
          overline="Total Outstanding Balance"
          value={formatCurrency(kpis.total_outstanding_balance || 0)}
          sub="Receivables + Payables"
          accent
        />
      </div>

      {/* Overdue Memo list */}
      {(kpis.overdue_memos || []).length > 0 && (
        <div className="mt-8 glass-card p-7 border border-[#C99356]/30" data-testid="overdue-memo-list">
          <div className="flex items-center gap-2 mb-1">
            <FileWarning className="w-4 h-4 text-[#C99356]" />
            <div className="overline text-[#C99356]">Action Required</div>
          </div>
          <h2 className="serif text-2xl mb-5">⚠ Overdue Memos</h2>
          <div className="space-y-2">
            {kpis.overdue_memos.map((m) => (
              <div key={m.id} className="grid grid-cols-12 gap-3 items-center py-3 border-b border-white/5 last:border-0 text-xs" data-testid={`overdue-row-${m.memo_number}`}>
                <div className="col-span-2 mono text-[#D4AF37]">{m.memo_number}</div>
                <div className="col-span-3 truncate">{m.customer_name}</div>
                <div className="col-span-2 mono text-[#8A8A8A] text-[0.7rem]">{m.customer_phone || "—"}</div>
                <div className="col-span-2 mono text-[#C99356]">due {m.due_date}</div>
                <div className="col-span-1 mono text-red-400 text-right">+{m.days_overdue}d</div>
                <div className="col-span-2 mono text-right">{formatCurrency(m.total_value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {kpis.role_view === "admin" && warehouseSummary?.staff_performance_by_warehouse && (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="staff-performance-section">
          {["ONLINE_SALE", "SHOWROOM", "LIVE_PRODUCT"].map((wh) => {
            const perf = warehouseSummary.staff_performance_by_warehouse[wh] || [];
            const WH_META = {
              ONLINE_SALE: { icon: Building2, color: "text-[#D4AF37]", label: "Online Sale" },
              SHOWROOM:    { icon: Store,     color: "text-[#7BA585]", label: "Showroom" },
              LIVE_PRODUCT:{ icon: Video,     color: "text-[#C99356]", label: "Live Product" },
            };
            const meta = WH_META[wh];
            const Icon = meta.icon;
            return (
              <div key={wh} className="glass-card p-7" data-testid={`staff-perf-${wh.toLowerCase()}`}>
                <div className="overline flex items-center gap-2 mb-1">
                  <Icon className={`w-3 h-3 ${meta.color}`} />
                  Top Sellers · {meta.label}
                </div>
                <h3 className="serif text-lg mb-5 text-[#8A8A8A]">This month</h3>
                {perf.length === 0 ? (
                  <div className="text-sm text-[#666] italic">No sales recorded yet.</div>
                ) : (
                  perf.map((s, idx) => (
                    <div key={s.seller_id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`text-[0.7rem] mono w-5 ${idx === 0 ? "text-[#D4AF37]" : "text-[#666]"}`}>#{idx + 1}</span>
                        <span className="text-sm truncate">{s.seller_name}</span>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="mono text-sm text-[#D4AF37]">{formatCurrency(s.revenue)}</div>
                        <div className="mono text-[0.6rem] text-[#666]">{s.sale_count} sales</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Revenue chart */}
      <div className="mt-10 glass-card p-7 lg:p-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="overline">Trailing 6 Months</div>
            <h2 className="serif text-2xl mt-1">Revenue & Profit</h2>
          </div>
          <div className="flex items-center gap-6 text-xs text-[#8A8A8A]">
            <div className="flex items-center gap-2">
              <span className="w-3 h-px bg-[#D4AF37]" /> Revenue
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-px bg-[#7BA585]" /> Profit
            </div>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer>
            <AreaChart data={monthly?.series || []}>
              <defs>
                <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.04)" />
              <XAxis
                dataKey="month"
                stroke="#666"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  background: "#111",
                  border: "1px solid #333",
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#D4AF37"
                strokeWidth={1.5}
                fill="url(#gold)"
              />
              <Line
                type="monotone"
                dataKey="profit"
                stroke="#7BA585"
                strokeWidth={1.5}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Lower panels */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Warehouses (count) */}
        <div className="glass-card p-7">
          <div className="overline">Distribution</div>
          <h3 className="serif text-xl mt-1 mb-6">By Warehouse</h3>
          {Object.entries(kpis.by_warehouse || {}).map(([wh, count]) => (
            <div key={wh} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
              <div className="flex items-center gap-3">
                {wh === "SHOWROOM"
                  ? <Store className="w-3.5 h-3.5 text-[#7BA585]" strokeWidth={1.25} />
                  : wh === "LIVE_PRODUCT"
                  ? <Video className="w-3.5 h-3.5 text-[#C99356]" strokeWidth={1.25} />
                  : <Building2 className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.25} />}
                <span className="text-sm">{wh === "SHOWROOM" ? "Showroom" : wh === "LIVE_PRODUCT" ? "Live Product" : "Online Sale"}</span>
              </div>
              <span className="mono text-lg text-[#D4AF37]">{count}</span>
            </div>
          ))}
        </div>

        {/* Status mix */}
        <div className="glass-card p-7">
          <div className="overline">Status Mix</div>
          <h3 className="serif text-xl mt-1 mb-6">Inventory State</h3>
          {Object.entries(kpis.by_status).map(([s, c]) => (
            <div key={s} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
              <span
                className={[
                  "status-badge",
                  s === "Available" && "status-available",
                  s === "Reserved" && "status-reserved",
                  s === "Sold" && "status-sold",
                ].filter(Boolean).join(" ")}
              >
                {s}
              </span>
              <span className="mono text-lg text-[#EAEAEA]">{c}</span>
            </div>
          ))}
        </div>

        {/* Low stock alerts */}
        <div className="glass-card p-7" data-testid="low-stock-panel">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-[#C99356]" strokeWidth={1.25} />
            <div className="overline">Low Stock</div>
          </div>
          <h3 className="serif text-xl mt-1 mb-6">Needs Attention</h3>
          {kpis.low_stock.length === 0 && (
            <div className="text-xs text-[#8A8A8A]">All inventory at healthy levels.</div>
          )}
          {kpis.low_stock.slice(0, 5).map((p) => (
            <div key={p.id} className="py-3 border-b border-white/5 last:border-0">
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="text-sm truncate">{p.name}</div>
                  <div className="mono text-[0.65rem] text-[#666] mt-1">{p.sku}</div>
                </div>
                <span className="mono text-xs text-[#C99356] shrink-0" data-testid={`low-stock-count-${p.sku}`}>
                  {p.stock} {p.stock === 1 ? "pc left" : "pcs left"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent sales */}
      <div className="mt-8 glass-card p-7 lg:p-10">
        <div className="overline">Atelier Pulse</div>
        <h3 className="serif text-2xl mt-1 mb-6">Recent Sales</h3>
        {kpis.recent_sales.length === 0 ? (
          <div className="text-sm text-[#8A8A8A]">No sales yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left overline text-[0.65rem]">
                  <th className="pb-4 font-normal">Invoice</th>
                  <th className="pb-4 font-normal">Piece</th>
                  <th className="pb-4 font-normal">Customer</th>
                  <th className="pb-4 font-normal text-right">Total</th>
                  <th className="pb-4 font-normal text-right">Profit</th>
                </tr>
              </thead>
              <tbody>
                {kpis.recent_sales.map((s) => (
                  <tr key={s.id} className="border-t border-white/5">
                    <td className="py-4 mono text-[#D4AF37] text-xs">{s.invoice_number}</td>
                    <td className="py-4">
                      {s.product_name}
                      <div className="text-[0.65rem] text-[#666] mono mt-0.5">
                        {formatQty(s.quantity, s.product_unit, s.product_custom_unit)}
                      </div>
                    </td>
                    <td className="py-4 text-[#8A8A8A]">{s.customer_name || "—"}</td>
                    <td className="py-4 mono text-right">{formatCurrency(s.total)}</td>
                    <td className="py-4 mono text-right text-[#7BA585]">{formatCurrency(s.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Commission widgets */}
      {commWidgets && (
        <div className="mt-8">
          <div className="overline">Sales Force</div>
          <h3 className="serif text-2xl mt-1 mb-6">Commission Overview</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Top sellers */}
            <div className="glass-card p-7" data-testid="widget-top-sellers">
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.25} />
                <div className="overline">Top Sellers · This Month</div>
              </div>
              {commWidgets.top_sellers.length === 0 ? (
                <div className="text-sm text-[#8A8A8A] mt-4">No commissioned sales yet.</div>
              ) : (
                <div className="mt-4 space-y-3">
                  {commWidgets.top_sellers.map((s, i) => (
                    <div key={s.seller_id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="mono text-xs text-[#D4AF37] w-5">#{i + 1}</span>
                        <div>
                          <div className="text-sm">{s.seller_name}</div>
                          <div className="text-[0.65rem] text-[#666] mono">{s.transactions} txn · {formatCurrency(s.total_sales)} sales</div>
                        </div>
                      </div>
                      <span className="mono text-sm text-[#D4AF37]">{formatCurrency(s.total_commission)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Total commission this month */}
            <div className="glass-card p-7" data-testid="widget-month-commission">
              <div className="flex items-center gap-2 mb-1">
                <Coins className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.25} />
                <div className="overline">Commission · This Month</div>
              </div>
              <div className="serif text-4xl mt-4 text-[#D4AF37]">{formatCurrency(commWidgets.total_commission_month)}</div>
              <div className="text-xs text-[#8A8A8A] mt-2">Generated since the 1st</div>
            </div>

            {/* Pending alert */}
            <div className="glass-card p-7" data-testid="widget-pending">
              <div className="flex items-center gap-2 mb-1">
                <Bell className="w-3.5 h-3.5 text-[#C99356]" strokeWidth={1.25} />
                <div className="overline">Pending Payouts</div>
              </div>
              <div className="serif text-4xl mt-4 text-[#C99356]">{formatCurrency(commWidgets.pending_total)}</div>
              <div className="text-xs text-[#8A8A8A] mt-2">{commWidgets.pending_count} outstanding · awaiting payment</div>
            </div>
          </div>

          {/* Performance chart */}
          {commWidgets.performance_series.length > 0 && (
            <div className="glass-card p-7 lg:p-10 mt-5" data-testid="widget-perf-chart">
              <div className="overline">Performance · Last 6 Months</div>
              <h4 className="serif text-xl mt-1 mb-6">Commission Generated</h4>
              <div className="h-56">
                <ResponsiveContainer>
                  <AreaChart data={commWidgets.performance_series}>
                    <defs>
                      <linearGradient id="commGold" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="month" stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} />
                    <Area type="monotone" dataKey="commission" stroke="#D4AF37" strokeWidth={1.5} fill="url(#commGold)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

function SellerDashboardView({ kpis, commWidgets }) {
  const progressPct = Math.min(100, kpis.target_progress_pct || 0);
  return (
    <Layout
      title="My Atelier"
      subtitle="Sales performance"
      action={
        <a
          href="/my-sales"
          data-testid="seller-cta-record-sale"
          className="btn-gold px-5 py-3 inline-flex items-center gap-2"
        >
          + Record Sale
        </a>
      }
    >
      {/* Personal KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          testid="seller-month-sales"
          overline="My Sales (Month)"
          value={formatCurrency(kpis.my_month_sales)}
          sub={`${kpis.my_month_count} transactions`}
          accent
        />
        <StatCard
          testid="seller-month-commission"
          overline="My Commission (Month)"
          value={formatCurrency(kpis.my_month_commission)}
          sub={`Pending: ${formatCurrency(kpis.pending_commission)}`}
        />
        <StatCard
          testid="seller-paid"
          overline="Commission Paid (All-time)"
          value={formatCurrency(kpis.paid_commission)}
          sub={`Pending: ${formatCurrency(kpis.pending_commission)}`}
        />
        <StatCard
          testid="seller-customers"
          overline="My Customers"
          value={kpis.my_customers_count}
          sub="Assigned to you"
          accent
        />
      </div>

      {/* Target progress */}
      <div className="mt-10 glass-card p-7 lg:p-10" data-testid="seller-target">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <div>
            <div className="overline">Monthly Target</div>
            <h3 className="serif text-2xl mt-1">{formatCurrency(kpis.target)}</h3>
          </div>
          <div className="text-right">
            <div className="serif text-3xl text-[#D4AF37]">{progressPct.toFixed(1)}%</div>
            <div className="text-xs text-[#8A8A8A] mt-1 mono">
              {formatCurrency(kpis.my_month_sales)} / {formatCurrency(kpis.target)}
            </div>
          </div>
        </div>
        <div className="mt-4 h-2 bg-white/5 overflow-hidden">
          <div
            className="h-full bg-[#D4AF37] transition-all duration-700"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="mt-3 text-[0.7rem] text-[#666] mono">
          {progressPct >= 100 ? "Target reached — congratulations." : `${formatCurrency(Math.max(0, kpis.target - kpis.my_month_sales))} to go.`}
        </div>
      </div>

      {/* Recent sales */}
      <div className="mt-8 glass-card p-7 lg:p-10">
        <div className="overline">Recent</div>
        <h3 className="serif text-2xl mt-1 mb-6">My Sales</h3>
        {kpis.recent_sales.length === 0 ? (
          <div className="text-sm text-[#8A8A8A]">
            No sales yet — record your first to start earning commission.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left overline text-[0.65rem]">
                  <th className="pb-4 font-normal">Invoice</th>
                  <th className="pb-4 font-normal">Piece</th>
                  <th className="pb-4 font-normal">Customer</th>
                  <th className="pb-4 font-normal text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {kpis.recent_sales.map((s) => (
                  <tr key={s.id} className="border-t border-white/5">
                    <td className="py-4 mono text-[#D4AF37] text-xs">{s.invoice_number}</td>
                    <td className="py-4">
                      {s.product_name}
                      <div className="text-[0.65rem] text-[#666] mono mt-0.5">
                        {formatQty(s.quantity, s.product_unit, s.product_custom_unit)}
                      </div>
                    </td>
                    <td className="py-4 text-[#8A8A8A]">{s.customer_name || "—"}</td>
                    <td className="py-4 mono text-right">{formatCurrency(s.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Commission widgets (own only) */}
      {commWidgets && commWidgets.performance_series.length > 0 && (
        <div className="mt-8 glass-card p-7 lg:p-10" data-testid="seller-perf-chart">
          <div className="overline">Performance · Last 6 Months</div>
          <h4 className="serif text-xl mt-1 mb-6">Commission Generated</h4>
          <div className="h-56">
            <ResponsiveContainer>
              <AreaChart data={commWidgets.performance_series}>
                <defs>
                  <linearGradient id="sellerGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="month" stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} />
                <Area type="monotone" dataKey="commission" stroke="#D4AF37" strokeWidth={1.5} fill="url(#sellerGold)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Layout>
  );
}
